import React from 'react';
import type { TableColumnsType } from 'antd';
import { Table, Tag } from 'antd';
import { useEnumLabel } from 'hooks/useEnumLabel';
import { formatApplyTimeWithTimezone } from 'utils/timezoneFormatter';
import { PricingSchemeApplyType } from '../graphql/types/PricingSchemeApplyType';

interface DSPPricingApplyRowProps {
    rowData: PricingSchemeApplyType[];
    loading?: boolean;
}

export const DSPPricingApplyColumns: React.FC<DSPPricingApplyRowProps> = ({
    rowData,
    loading
}) => {
    const est = useEnumLabel('enum.pricingApplyEffectStatus');

    const renderEffectStatus = (status: string) => {
        const statusConfig = {
            'EFFECTIVE': { color: 'success' },
            'PENDING': { color: 'processing' },
            'EXPIRED': { color: 'default' },
            'UNBOUND': { color: 'default' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default' };
        // UNBOUND 归一化为 EXPIRED 展示：与老系统"已解绑记录展示为已失效"的口径保持一致
        return <Tag color={config.color}>{est(status === 'UNBOUND' ? 'EXPIRED' : status)}</Tag>;
    };

    // 绑定状态的Tag渲染函数：后端现在吐原始 code（"1"-已绑定/"0"-已解绑），展示文案在前端维护
    const renderBindStatus = (status: string) => {
        const statusConfig = {
            '1': { color: 'green', text: '已绑定' },
            '0': { color: 'red', text: '已解绑' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns: TableColumnsType<PricingSchemeApplyType> = [
        {
            title: '方案ID',
            dataIndex: 'pricingSchemeId',
            key: 'pricingSchemeId',
            align: 'left',
        },
        {
            title: '方案名称',
            dataIndex: 'pricingSchemeName',
            key: 'pricingSchemeName',
            align: 'left',
        },
        {
            title: '绑定时间',
            dataIndex: 'bindTime',
            key: 'bindTime',
            align: 'left',
            render: (bindTime: string | null, record: PricingSchemeApplyType) =>
                formatApplyTimeWithTimezone(bindTime, record.timezone),
        },
        {
            title: '生效时间',
            dataIndex: 'effectiveTime',
            key: 'effectiveTime',
            align: 'left',
            render: (effectiveTime: string | null, record: PricingSchemeApplyType) => {
                const expiration = record.expirationTime
                    ? formatApplyTimeWithTimezone(record.expirationTime, record.timezone)
                    : '长期';
                return <span>{formatApplyTimeWithTimezone(effectiveTime, record.timezone) + '-' + expiration}</span>;
            },
        },
        {
            title: '时区',
            dataIndex: 'timezone',
            key: 'timezone',
            align: 'left',
        },
        {
            title: '生效状态',
            dataIndex: 'effectStatus',
            key: 'effectStatus',
            align: 'left',
            render: (status: string) => renderEffectStatus(status),
        },
        {
            title: '绑定状态',
            dataIndex: 'bindStatus',
            key: 'bindStatus',
            align: 'left',
            render: (status: string) => renderBindStatus(status),
        },
        {
            title: '操作人',
            dataIndex: 'operatorName',
            key: 'operatorName',
            align: 'left',
        },
        {
            title: 'DSP客户维度',
            dataIndex: 'bindValueDesc',
            key: 'bindValueDesc',
            align: 'left',
            render: (value: string | undefined, record: PricingSchemeApplyType) => value || record.bindValue,
        },
    ];

    return (
        <Table
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={rowData}
            scroll={{ x: 1500 }}
            size="middle"
            className="mb-4 [&_.ant-table-cell]:break-words [&_.ant-table-cell]:whitespace-normal"
            pagination={false}
            loading={loading}
        />
    );
};
